import {
  CHAIN_ID,
  CHAIN_PARAMS,
  WALLETCONNECT_PROJECT_ID,
} from '../constants/constants';
import { Eip1193Provider } from './walletProvider';

/**
 * Подключение через WalletConnect — для телефона, где в обычном браузере
 * расширения нет. Кошелёк живёт в своём приложении, панель говорит с ним через
 * релей Reown: на телефоне — переходом в приложение, на компьютере — QR-кодом.
 *
 * Провайдер — тот же EIP-1193, что у расширения, поэтому дальше по коду
 * разницы между ними нет. Библиотека тяжёлая и грузится по требованию: по
 * клику на кнопку или при восстановлении прошлой сессии.
 */

export interface WalletConnectProvider extends Eip1193Provider {
  accounts: string[];
  session?: unknown;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

const CHAIN_ID_NUMBER = Number(CHAIN_ID);

/**
 * Пометка «в прошлый раз подключались через WalletConnect». Без неё пришлось
 * бы грузить библиотеку на каждом открытии панели ради проверки сессии.
 */
const SESSION_FLAG = 'salary:walletconnect';

let providerPromise: Promise<WalletConnectProvider> | null = null;

const readFlag = (): boolean => {
  try {
    return localStorage.getItem(SESSION_FLAG) === '1';
  } catch {
    return false;
  }
};

const writeFlag = (value: boolean) => {
  try {
    if (value) localStorage.setItem(SESSION_FLAG, '1');
    else localStorage.removeItem(SESSION_FLAG);
  } catch {
    // Хранилище недоступно (приватный режим) — сессия просто не переживёт
    // перезагрузку, подключиться можно заново.
  }
};

const loadProvider = (): Promise<WalletConnectProvider> => {
  if (!providerPromise) {
    providerPromise = import('@walletconnect/ethereum-provider')
      .then(
        ({ EthereumProvider }) =>
          EthereumProvider.init({
            projectId: WALLETCONNECT_PROJECT_ID,
            // Обязательная сеть: кошелёк, не умеющий BSC, откажет сразу, а не
            // на первой транзакции.
            chains: [CHAIN_ID_NUMBER],
            rpcMap: { [CHAIN_ID_NUMBER]: CHAIN_PARAMS[0].rpcUrls[0] },
            showQrModal: true,
            metadata: {
              name: 'EMIVN Salary',
              description: 'Зарплатный проект EMIVN',
              url: window.location.origin,
              icons: [`${window.location.origin}/logo192.png`],
            },
          }) as unknown as Promise<WalletConnectProvider>,
      )
      .catch((error) => {
        // Неудачную загрузку не кэшируем: следующий клик попробует снова.
        providerPromise = null;
        throw error;
      });
  }

  return providerPromise;
};

/** Восстановление сессии после перезагрузки. Окно не открывает. */
export const restoreWalletConnect =
  async (): Promise<WalletConnectProvider | null> => {
    if (!readFlag()) return null;

    try {
      const provider = await loadProvider();
      if (provider.session && provider.accounts.length > 0) return provider;
    } catch (error) {
      console.error('WalletConnect restore:', error);
    }

    writeFlag(false);
    return null;
  };

/** Открывает окно выбора кошелька и ждёт подключения. */
export const connectWalletConnect =
  async (): Promise<WalletConnectProvider> => {
    let provider: WalletConnectProvider;

    try {
      provider = await loadProvider();
    } catch (error) {
      console.error('WalletConnect init:', error);
      throw new Error('Не удалось загрузить WalletConnect. Обновите страницу.');
    }

    if (!provider.session) {
      try {
        await provider.connect();
      } catch (error) {
        console.error('WalletConnect connect:', error);
        // Закрытое окно выбора приходит ошибкой без кода — это отказ, а не сбой.
        throw new Error('Подключение через WalletConnect отменено');
      }
    }

    writeFlag(true);
    return provider;
  };

/** Разрыв сессии: без него кошелёк продолжит показывать панель подключённой. */
export const disconnectWalletConnect = async (
  provider: WalletConnectProvider,
) => {
  writeFlag(false);

  try {
    await provider.disconnect();
  } catch (error) {
    console.warn('WalletConnect disconnect:', (error as Error)?.message);
  }
};
